import * as React from 'react'

import { Icon, Spinner } from '@factorialco/deprecated-design-system'
import {
  ApprovalMaterializedApprovalFragment,
  GetMaterializedApprovalsFlowsDocument,
  MaterializedApprovalFlowPolicyFragment,
} from 'generated/resources/graphql'
import { isEmpty } from 'lodash'

import { useLazyQuery } from 'modules/core/hooks/useQuery'

import {
  BaseRequestItem,
  RequestComponentProps,
} from 'modules/notifications/pages/RequestsPage/RequestItems/RequestItem/base'

import ActionButtons from './components/ActionButtons'
import CollapseContent from './components/CollapseContent'
import { ApprovalData } from './types'

export interface PendingApprovalsProps extends RequestComponentProps {
  getApprovalData?: (
    flow: ApprovalMaterializedApprovalFragment,
    policies?: MaterializedApprovalFlowPolicyFragment | null
  ) => Promise<object>
  collapseContentBlocks?: (data: ApprovalData) => {
    leftBlock?: JSX.Element
    rightBlock?: JSX.Element
  }
  icon?: React.ComponentProps<typeof Icon.Medium>['icon']
  onSuccess?: (action: string, reason?: string) => void
}

const BasePendingApproval = ({
  request,
  index,
  compact,
  getApprovalData,
  collapseContentBlocks,
  removeRequestById,
  icon,
  onSuccess,
}: PendingApprovalsProps) => {
  const [loadingData, setLoadingData] = React.useState<boolean>(false)
  const [collapseContentData, setCollapseContentData] = React.useState<ApprovalData>()
  const [fetchApprovalFlow, {}] = useLazyQuery(GetMaterializedApprovalsFlowsDocument)
  /*
   * Get the approval data and the custom data from inherited components and set them into the state
   */
  const setOpenContentData = async (
    flow: ApprovalMaterializedApprovalFragment,
    policies?: MaterializedApprovalFlowPolicyFragment | null
  ) => {
    const customApprovalData = getApprovalData && (await getApprovalData(flow, policies))
    setCollapseContentData({
      approvalFlow: flow,
      ...(customApprovalData ? customApprovalData : {}),
    })
  }

  /*
   * Executed when the user click the open collapse button.
   *
   * If the data is empty, we request the data. This way allow us to only request once
   * the data. When the user open/close multiple times this content, the information that
   * we'll use will be the first one
   *
   * If the flow doesn't exist, we don't requested the data and recharge the tasks
   */
  const collapseOnOpen = async () => {
    if (isEmpty(collapseContentData)) {
      setLoadingData(true)
      const { data } = await fetchApprovalFlow({
        variables: { ids: [String(request.resourceIdentifier)] },
      })

      const flow = data?.approvals.materializedApprovalsFlowsConnection.nodes?.[0]
      if (!flow) {
        removeRequestById(request.id)
      } else {
        const policies = flow.owner?.employee?._policies

        await setOpenContentData(flow, policies)
      }
      setLoadingData(false)
    }
  }

  /*
   * Adds the props to collapseContent
   */
  const collapseBlockOptions = () => {
    if (isEmpty(collapseContentData) || !collapseContentBlocks) return null

    const { leftBlock, rightBlock } = collapseContentBlocks(collapseContentData)

    return {
      leftBlock,
      rightBlock,
    }
  }

  return (
    <BaseRequestItem
      url={String(request.actionUrl)}
      request={request}
      index={index}
      compact={compact}
      buttons={
        <ActionButtons
          id={String(request.resourceIdentifier)}
          requestId={String(request.id)}
          removeRequestById={removeRequestById}
          onSuccess={onSuccess}
        />
      }
      collapseContent={
        loadingData && isEmpty(collapseContentData) ? (
          <Spinner />
        ) : (
          <CollapseContent
            collapseContentData={collapseContentData!}
            blocks={collapseBlockOptions()}
          />
        )
      }
      onOpen={collapseOnOpen}
      icon={icon}
    />
  )
}

export default BasePendingApproval
